import styled, { css } from "styled-components";

import { semiBoldAlias, makePalette } from "Libs/theme";

type LabeledThreshold = { label: string };
type ThresholdKind = number | LabeledThreshold | string;

type ThemeType = {
  [key: string]: string;
};

const hiddenThresholds = [0.8, 0.9];

const THRESHOLD_COLORS = {
  numeric: {
    1: (theme: ThemeType) => theme.threshold__max,
    0.9: (theme: ThemeType) => theme.threshold__max,
    0.2: (theme: ThemeType) => theme.threshold__20,
    0.8: (theme: ThemeType) => theme.threshold__warning
  },
  labeled: {
    "autoscaling-lower": (theme: ThemeType) =>
      theme.threshold__autoscaling__lower,
    "autoscaling-upper": (theme: ThemeType) =>
      theme.threshold__autoscaling__upper,
    overcommit: (theme: ThemeType) => theme.threshold__max
  }
};

const getStroke = (
  kind: ThresholdKind,
  theme: ThemeType,
  isAutoscalingEnabled: boolean,
  resourceName?: string,
  serviceKind?: string
) => {
  if (!kind || !theme) {
    return theme?.threshold__none || "transparent";
  }
  if (
    isAutoscalingEnabled &&
    typeof kind === "number" &&
    hiddenThresholds.includes(kind) &&
    resourceName === "cpu" &&
    serviceKind === "Webapp"
  ) {
    return "none";
  }

  if (
    isAutoscalingEnabled &&
    kind === 1 &&
    resourceName === "cpu" &&
    serviceKind === "Webapp"
  ) {
    return theme.threshold__none;
  }

  const isNumeric = typeof kind === "number";
  const lookupKey = isNumeric ? kind : (kind as LabeledThreshold).label;
  const colorMap = isNumeric
    ? THRESHOLD_COLORS.numeric
    : THRESHOLD_COLORS.labeled;

  return colorMap[lookupKey]?.(theme) || theme.threshold__none;
};

const getStrokeDashArray = (kind: ThresholdKind) => {
  switch (kind) {
    case "overcommit":
    case 0.8:
    case 0.9:
      return "4 10";
    default:
      return "none";
  }
};

const colorDefinitions = palette => ({
  average_gradient__stop_1: {
    default: palette.slate,
    dark: palette.snow,
    contrast: "#01001a"
  },
  host_gradient__stop_1: {
    default: palette.granite,
    contrast: "#8c94a1"
  },
  threshold__warning: {
    default: palette.gold,
    contrast: "#b88d00"
  },
  threshold__max: {
    default: palette.red,
    contrast: "#e50000"
  },
  threshold__80: {
    default: "var(--mode-bg-primary-default)",
    contrast: "#e50000"
  },
  threshold__autoscaling__lower: {
    default: "var(--mode-bg-primary-default)",
    contrast: "#e50000"
  },
  threshold__autoscaling__upper: {
    default: "var(--mode-bg-primary-default)",
    contrast: "#e50000"
  },
  threshold__20: {
    default: "var(--mode-bg-primary-default)",
    contrast: "#e50000"
  },
  threshold__none: {
    default: palette.periwinkleGrey
  },
  text: {
    default: palette.granite,
    dark: palette.periwinkleGrey,
    contrast: palette.ebonyLight
  }
});

const theme = makePalette(colorDefinitions);

export const YAxis = styled.g``;

export const Tick = styled.text`
  stroke-width: 0.5;
  font-size: 10px;
  ${semiBoldAlias()};
  user-select: none;
  line-height: 22px;
  fill: ${theme.text};
  pointer-events: none;
`;

export const Threshold = styled.path<{
  $kind: ThresholdKind;
  $isAutoscalingEnabled: boolean;
  $resourceName?: string;
  $serviceKind?: string;
}>`
  stroke: ${({ $kind, $isAutoscalingEnabled, $resourceName, $serviceKind }) =>
    getStroke(
      $kind,
      theme,
      $isAutoscalingEnabled,
      $resourceName,
      $serviceKind
    )};
  stroke-dasharray: ${({ $kind }) => getStrokeDashArray($kind)};
  fill: none;
  stroke-width: 0.5;
  pointer-events: none;
`;

const __label = css`
  ${semiBoldAlias()};
  font-size: 10px;
  line-height: 22px;
  text-align: right;
  text-transform: lowercase;
  text-anchor: end;
  pointer-events: none;
  user-select: none;
`;

export const CriticalLabel = styled.text`
  ${__label};
  fill: ${theme.threshold__max};
`;

export const WarningLabel = styled.text`
  ${__label};
  fill: ${theme.threshold__warning};
`;

export const FilledArea = styled.path`
  fill: var(--mode-bg-primary-weaker);
  opacity: 0.2;
  pointer-events: none;
`;
